/**
 * AdminDashboard Component
 * Main admin panel with user and facility management
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatServerDateTime } from '../utils/dateFormat';
import './AdminDashboard.css';
import PendingRegistrations from './PendingRegistrations';
import RegistrationSettings from './RegistrationSettings';
import TechnologistManagement from './TechnologistManagement';
import PermanentDeleteModal from './PermanentDeleteModal';

function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('users');
  const [pendingCount, setPendingCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loginAudit, setLoginAudit] = useState(null);
  const [bugReports, setBugReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showFacilityForm, setShowFacilityForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingFacility, setEditingFacility] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteBlockers, setDeleteBlockers] = useState([]);

  // Form states
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    fullName: '',
    jobTitle: '',
    role: 'user',
    facilityId: '',
    professionRole: 'sonographer',
    defaultTechnologistId: ''
  });
  const [formTechnologists, setFormTechnologists] = useState([]);

  const [facilityForm, setFacilityForm] = useState({
    name: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    reportFormat: 'pdf'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
      loadStats();
    } else if (activeTab === 'facilities') {
      loadFacilities();
    } else if (activeTab === 'analytics') {
      loadAnalytics();
      loadLoginAudit();
    } else if (activeTab === 'bugReports') {
      loadBugReports();
    }
  }, [activeTab]);

  useEffect(() => {
    loadFacilities(); // Always load facilities for user form dropdown
  }, []);

  // Reload technologist dropdown when facility or role changes in the user form
  useEffect(() => {
    if (userForm.facilityId) {
      axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/technologists?facilityId=${userForm.facilityId}&isActive=true&professionRole=${userForm.professionRole || 'sonographer'}`,
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(r => setFormTechnologists(r.data.technologists || []))
       .catch(() => setFormTechnologists([]));
    } else {
      setFormTechnologists([]);
    }
  }, [userForm.facilityId, userForm.professionRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadFacilities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/facilities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFacilities(response.data.facilities || []);
    } catch (error) {
      console.error('Error loading facilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/users/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/analytics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnalytics(response.data.analytics);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadLoginAudit = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/login-audit`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLoginAudit({ recent: response.data.recent, recentFailures: response.data.recentFailures });
    } catch (error) {
      console.error('Error loading login audit:', error);
    }
  };

  const handleDeleteBugReport = async (reportId) => {
    if (!window.confirm('Delete this bug report? This cannot be undone.')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/bug-reports/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadBugReports();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete bug report');
    }
  };

  const loadBugReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/bug-reports`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBugReports(response.data.reports || []);
    } catch (error) {
      console.error('Error loading bug reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/users`,
        userForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`User created!\nUsername: ${response.data.user.username}\nTemporary Password: ${response.data.temporaryPassword}\n\nPlease save this password - it will not be shown again!`);
      setShowUserForm(false);
      setUserForm({ username: '', email: '', fullName: '', jobTitle: '', role: 'user', facilityId: '', professionRole: 'sonographer', defaultTechnologistId: '', expiresAt: '' });
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${editingUser.id}`,
        userForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('User updated successfully!');
      setEditingUser(null);
      setShowUserForm(false);
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleEditUser = async (user) => {
    try {
      // Fetch full user details including settings (default_technologist_id)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const u = response.data.user;
      const settings = u.settings;
      setUserForm({
        username: u.username || '',
        email: u.email || '',
        fullName: u.full_name || '',
        jobTitle: u.job_title || '',
        role: u.role || 'user',
        facilityId: u.facility_id ? String(u.facility_id) : '',
        professionRole: settings?.profession_role === 'nurse' ? 'nurse' : 'sonographer',
        defaultTechnologistId: settings?.default_technologist_id ? String(settings.default_technologist_id) : '',
        expiresAt: u.expires_at ? u.expires_at.slice(0, 10) : ''
      });
      setEditingUser(u);
      setShowUserForm(true);
    } catch (error) {
      alert('Failed to load user details');
    }
  };

  const handleCheckDeletion = async (user) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${user.id}/can-delete`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserToDelete(user);
      setDeleteBlockers(response.data.blockers || []);
      setShowDeleteModal(true);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to check deletion eligibility');
    }
  };

  const handlePermanentDelete = async (confirmUsername) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userToDelete.id}/permanent-delete`,
        { confirmUsername },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`User "${userToDelete.username}" has been permanently deleted.`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      loadUsers();
      loadStats();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to permanently delete user');
    }
  };

  const handleReactivateUser = async (userId) => {
    if (!window.confirm('Reactivate this user?')) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadUsers();
      loadStats();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to reactivate user');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('User deactivated successfully!');
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Reset this user\'s password?')) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Password reset!\nNew temporary password: ${response.data.temporaryPassword}\n\nPlease give this to the user.`);
    } catch (error) {
      alert('Failed to reset password');
    }
  };

  const handleCreateFacility = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/facilities`,
        facilityForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Facility created successfully!');
      setShowFacilityForm(false);
      setFacilityForm({ name: '', addressLine1: '', city: '', state: '', zipCode: '', phone: '', reportFormat: 'pdf' });
      loadFacilities();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create facility');
    }
  };

  const handleUpdateFacility = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/facilities/${editingFacility.id}`,
        facilityForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Facility updated successfully!');
      setEditingFacility(null);
      setShowFacilityForm(false);
      setFacilityForm({ name: '', addressLine1: '', city: '', state: '', zipCode: '', phone: '', reportFormat: 'pdf' });
      loadFacilities();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update facility');
    }
  };

  const handleEditFacility = (facility) => {
    setEditingFacility(facility);
    setFacilityForm({
      name: facility.name || '',
      addressLine1: facility.address_line1 || '',
      city: facility.city || '',
      state: facility.state || '',
      zipCode: facility.zip_code || '',
      phone: facility.phone || '',
      reportFormat: facility.report_format || 'pdf'
    });
    setShowFacilityForm(true);
  };

  const handleDeleteFacility = async (facility) => {
    const userCount = facility.user_count || 0;
    
    if (userCount > 0) {
      // Facility has users - give option to unassign
      const unassign = window.confirm(
        `⚠️ This facility has ${userCount} assigned user(s).\n\n` +
        `Click OK to unassign all users and delete the facility.\n` +
        `Users will remain active but will not be assigned to any facility.\n\n` +
        `Click Cancel to abort.`
      );
      
      if (!unassign) return;
      
      try {
        // Unassign users first
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/facilities/${facility.id}/unassign`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Then delete facility
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/admin/facilities/${facility.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        alert(`Facility deleted successfully! ${userCount} user(s) unassigned.`);
        loadFacilities();
        loadUsers(); // Refresh users list if on users tab
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete facility');
      }
    } else {
      // No users - simple delete confirmation
      if (!window.confirm(`Delete facility "${facility.name}"?`)) return;
      
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/admin/facilities/${facility.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Facility deleted successfully!');
        loadFacilities();
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete facility');
      }
    }
  };

  return (
    <>
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="header-actions">
          <a href="/" className="btn-back">Back to Worksheets</a>
          <button onClick={onLogout} className="btn-logout">Logout</button>
        </div>
      </div>

      <div className="admin-tabs">
        <button 
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={activeTab === 'facilities' ? 'active' : ''}
          onClick={() => setActiveTab('facilities')}
        >
          Facilities
        </button>
        <button 
          className={activeTab === 'technologists' ? 'active' : ''}
          onClick={() => setActiveTab('technologists')}
        >
          Technologists
        </button>
        <button 
          className={activeTab === 'registrations' ? 'active' : ''}
          onClick={() => setActiveTab('registrations')}
        >
          Registration Requests {pendingCount > 0 && <span className="badge-count">{pendingCount}</span>}
        </button>
        <button
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        <button
          className={activeTab === 'analytics' ? 'active' : ''}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
        <button
          className={activeTab === 'bugReports' ? 'active' : ''}
          onClick={() => setActiveTab('bugReports')}
        >
          Bug Reports
        </button>
      </div>

      {activeTab === 'users' && (
        <div className="tab-content">
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <h3>{stats.total}</h3>
                <p>Total Users</p>
              </div>
              <div className="stat-card">
                <h3>{stats.active}</h3>
                <p>Active</p>
              </div>
              <div className="stat-card">
                <h3>{stats.admins}</h3>
                <p>Admins</p>
              </div>
              <div className="stat-card">
                <h3>{stats.regularUsers}</h3>
                <p>Regular Users</p>
              </div>
            </div>
          )}

          <div className="actions">
            <button onClick={() => { setShowUserForm(true); setEditingUser(null); }} className="btn-primary">
              + Create User
            </button>
          </div>

          {showUserForm && (
            <div className="form-panel">
              <h3>
                {editingUser ? 'Edit User' : 'Create New User'}
                {editingUser?.is_student ? <span className="badge badge-student">🎓 Student</span> : null}
              </h3>
              <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Username <small>(optional — auto-generated from full name if blank)</small></label>
                    <input
                      type="text"
                      value={userForm.username}
                      onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                      disabled={editingUser}
                      placeholder="Leave blank to auto-generate"
                    />
                  </div>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={userForm.fullName}
                      onChange={(e) => setUserForm({...userForm, fullName: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Job Title</label>
                    <input
                      type="text"
                      value={userForm.jobTitle}
                      onChange={(e) => setUserForm({...userForm, jobTitle: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                      required
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Facility</label>
                    <select
                      value={userForm.facilityId}
                      onChange={(e) => setUserForm({...userForm, facilityId: e.target.value, defaultTechnologistId: ''})}
                    >
                      <option value="">Select Facility</option>
                      {facilities.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {editingUser && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Profession</label>
                      <select
                        value={userForm.professionRole}
                        onChange={(e) => setUserForm({...userForm, professionRole: e.target.value, defaultTechnologistId: ''})}
                      >
                        <option value="sonographer">Sonographer</option>
                        <option value="nurse">Nurse</option>
                      </select>
                      <small>Determines which worksheets this user sees and which technologist list they're matched against.</small>
                    </div>
                    <div className="form-group">
                      <label>Technologist Assignment</label>
                      {formTechnologists.length > 0 ? (
                        <>
                          <select
                            value={userForm.defaultTechnologistId}
                            onChange={(e) => setUserForm({...userForm, defaultTechnologistId: e.target.value})}
                          >
                            <option value="">— None —</option>
                            {formTechnologists.map(t => (
                              <option key={t.id} value={t.id}>{t.full_name}</option>
                            ))}
                          </select>
                          <small>Links this user account to a technologist entry in the dropdown list.</small>
                        </>
                      ) : (
                        <small>
                          {userForm.facilityId
                            ? 'No technologists found for this facility and role. Add one in Technologist Management, or check the Profession selected above.'
                            : 'This user has no facility assigned, so there are no matching technologists to choose from. Assign a facility above, or add a system-wide technologist in Technologist Management.'}
                        </small>
                      )}
                    </div>
                  </div>
                )}

                {editingUser && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Account Expiry</label>
                      <input
                        type="date"
                        value={userForm.expiresAt}
                        onChange={(e) => setUserForm({...userForm, expiresAt: e.target.value})}
                      />
                      {userForm.expiresAt && (
                        <button
                          type="button"
                          className="btn-sm"
                          onClick={() => setUserForm({...userForm, expiresAt: ''})}
                        >
                          Clear (never expires)
                        </button>
                      )}
                      <small>
                        {userForm.expiresAt
                          ? 'This account will automatically deactivate at the end of this date.'
                          : 'No expiry set — this account stays active until manually deactivated.'}
                      </small>
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                  <button type="button" onClick={() => { setShowUserForm(false); setEditingUser(null); setUserForm({ username: '', email: '', fullName: '', jobTitle: '', role: 'user', facilityId: '', professionRole: 'sonographer', defaultTechnologistId: '', expiresAt: '' }); }} className="btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <table className="data-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Facility</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>
                    {user.full_name}
                    {user.is_student ? <span className="badge badge-student" title="Temporary student account">🎓 Student</span> : null}
                  </td>
                  <td>{user.email || '-'}</td>
                  <td><span className={`badge badge-${user.role}`}>{user.role}</span></td>
                  <td>{user.facility_name || '-'}</td>
                  <td>
                    <span className={`status-${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {user.is_active && user.expires_at && (
                      <div className="expiry-note">expires {user.expires_at.slice(0, 10)}</div>
                    )}
                  </td>
                  <td>
                    <div className="user-actions">
                      <div className="user-actions-row">
                        <button onClick={() => handleEditUser(user)} className="btn-sm btn-primary-sm">Edit</button>
                        <button onClick={() => handleResetPassword(user.id)} className="btn-sm">Reset Password</button>
                      </div>
                      <div className="user-actions-row">
                        {user.is_active ? (
                          <button onClick={() => handleDeactivateUser(user.id)} className="btn-sm btn-warning">Deactivate</button>
                        ) : (
                          <button onClick={() => handleReactivateUser(user.id)} className="btn-sm btn-success">Reactivate</button>
                        )}
                        <button onClick={() => handleCheckDeletion(user)} className="btn-sm btn-danger">Permanent Delete</button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'facilities' && (
        <div className="tab-content">
          <div className="actions">
            <button onClick={() => setShowFacilityForm(true)} className="btn-primary">
              + Create Facility
            </button>
          </div>

          {showFacilityForm && (
            <div className="form-panel">
              <h3>{editingFacility ? 'Edit Facility' : 'Create New Facility'}</h3>
              <form onSubmit={editingFacility ? handleUpdateFacility : handleCreateFacility}>
                <div className="form-group">
                  <label>Facility Name *</label>
                  <input
                    type="text"
                    value={facilityForm.name}
                    onChange={(e) => setFacilityForm({...facilityForm, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={facilityForm.addressLine1}
                    onChange={(e) => setFacilityForm({...facilityForm, addressLine1: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={facilityForm.city}
                      onChange={(e) => setFacilityForm({...facilityForm, city: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      value={facilityForm.state}
                      onChange={(e) => setFacilityForm({...facilityForm, state: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Zip Code</label>
                    <input
                      type="text"
                      value={facilityForm.zipCode}
                      onChange={(e) => setFacilityForm({...facilityForm, zipCode: e.target.value})}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={facilityForm.phone}
                    onChange={(e) => setFacilityForm({...facilityForm, phone: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Report Format</label>
                  <select
                    value={facilityForm.reportFormat}
                    onChange={(e) => setFacilityForm({...facilityForm, reportFormat: e.target.value})}
                  >
                    <option value="pdf">PDF (one file per report)</option>
                    <option value="png">PNG (one image per page, for PACS that reject PDFs)</option>
                  </select>
                  <small>Applies to every user at this facility the next time they log in. Technologists cannot change it.</small>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn-primary">{editingFacility ? 'Update' : 'Create'}</button>
                  <button type="button" onClick={() => { setShowFacilityForm(false); setEditingFacility(null); }} className="btn-secondary">Cancel</button>
                </div>
              </form>
            </div>
          )}

          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>City, State</th>
                <th>Phone</th>
                <th>Report Format</th>
                <th>Users</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map(facility => (
                <tr key={facility.id}>
                  <td>{facility.name}</td>
                  <td>{facility.address_line1 || '-'}</td>
                  <td>{facility.city && facility.state ? `${facility.city}, ${facility.state}` : '-'}</td>
                  <td>{facility.phone || '-'}</td>
                  <td>{(facility.report_format || 'pdf').toUpperCase()}</td>
                  <td>{facility.user_count || 0}</td>
                  <td>
                    <button onClick={() => handleEditFacility(facility)} className="btn-sm">Edit</button>
                    <button onClick={() => handleDeleteFacility(facility)} className="btn-sm btn-danger">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'technologists' && (
        <div className="tab-content">
          <TechnologistManagement />
        </div>
      )}

      {activeTab === 'registrations' && (
        <div className="tab-content">
          <PendingRegistrations />
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="tab-content">
          <RegistrationSettings />
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="tab-content">
          {!analytics ? (
            <p>Loading analytics...</p>
          ) : (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>{analytics.totalAll}</h3>
                  <p>Total Reports Generated</p>
                </div>
                <div className="stat-card">
                  <h3>{analytics.total30}</h3>
                  <p>Last 30 Days</p>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Reports by Exam Type (all time)</h3>
                {analytics.byType.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)' }}>No data yet.</p>
                ) : analytics.byType.map(row => {
                  const pct = analytics.totalAll > 0
                    ? Math.round((row.count / analytics.totalAll) * 100)
                    : 0;
                  return (
                    <div key={row.exam_type} className="analytics-row">
                      <span className="analytics-label">{row.exam_type}</span>
                      <div className="analytics-bar-track">
                        <div className="analytics-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="analytics-count">{row.count}</span>
                    </div>
                  );
                })}
              </div>

              <div className="analytics-section">
                <h3>Top Users — Last 30 Days</h3>
                {analytics.byUser.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)' }}>No data yet.</p>
                ) : analytics.byUser.map(row => {
                  const pct = analytics.total30 > 0
                    ? Math.round((row.count / analytics.total30) * 100)
                    : 0;
                  return (
                    <div key={row.username} className="analytics-row">
                      <span className="analytics-label">{row.username}</span>
                      <div className="analytics-bar-track">
                        <div className="analytics-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="analytics-count">{row.count}</span>
                    </div>
                  );
                })}
              </div>

              <div className="analytics-section">
                <h3>Recent Activity (last 20)</h3>
                {analytics.recent.length === 0 ? (
                  <p style={{ color: 'var(--color-text-secondary)' }}>No activity yet.</p>
                ) : (
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Exam Type</th>
                        <th>Date/Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent.map((row, i) => (
                        <tr key={i}>
                          <td>{row.username || '—'}</td>
                          <td>{row.exam_type}</td>
                          <td>{formatServerDateTime(row.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="analytics-section">
                <h3>Recent Login Activity</h3>
                {!loginAudit ? (
                  <p style={{ color: 'var(--color-text-secondary)' }}>Loading...</p>
                ) : (
                  <>
                    {loginAudit.recentFailures > 0 && (
                      <p style={{ color: 'var(--color-danger)', fontWeight: 600 }}>
                        {loginAudit.recentFailures} failed login attempt{loginAudit.recentFailures === 1 ? '' : 's'} in the last 24 hours
                      </p>
                    )}
                    {loginAudit.recent.length === 0 ? (
                      <p style={{ color: 'var(--color-text-secondary)' }}>No login activity yet.</p>
                    ) : (
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Result</th>
                            <th>Date/Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginAudit.recent.map((row, i) => (
                            <tr key={i}>
                              <td>{row.username || '—'}</td>
                              <td>
                                <span className={`status-${row.success ? 'active' : 'inactive'}`}>
                                  {row.success ? 'Success' : 'Failed'}
                                </span>
                              </td>
                              <td>{formatServerDateTime(row.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'bugReports' && (
        <div className="tab-content">
          {loading ? (
            <p>Loading bug reports...</p>
          ) : bugReports.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>No bug reports yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reported By</th>
                  <th>Description</th>
                  <th>Page</th>
                  <th>Date/Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bugReports.map(report => (
                  <tr key={report.id}>
                    <td>{report.username || '—'}</td>
                    <td style={{ whiteSpace: 'pre-wrap', maxWidth: '400px' }}>{report.description}</td>
                    <td style={{ wordBreak: 'break-all', maxWidth: '200px' }}>{report.url || '—'}</td>
                    <td>{formatServerDateTime(report.created_at)}</td>
                    <td>
                      <button onClick={() => handleDeleteBugReport(report.id)} className="btn-sm btn-danger">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>

    {showDeleteModal && userToDelete && (
      <PermanentDeleteModal
        user={userToDelete}
        blockers={deleteBlockers}
        onConfirm={handlePermanentDelete}
        onCancel={() => { setShowDeleteModal(false); setUserToDelete(null); }}
      />
    )}
    </>
  );
}

export default AdminDashboard;
